import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../lib/api';
import { fmtDate } from '../lib/format';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { DatabaseBackup, Download, RotateCcw, Upload, RefreshCw, ShieldCheck, AlertTriangle, CalendarClock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import PageHelp from '../components/PageHelp';

const fmtBytes = (n) => {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};
const kindBadge = (k) => ({ scheduled: 'secondary', manual: 'default', safety: 'outline', uploaded: 'outline' }[k] || 'secondary');
const statusBadge = (s) => s === 'completed'
  ? <Badge className="bg-success text-white">Completed</Badge>
  : s === 'running' ? <Badge className="bg-amber-500 text-white">Running…</Badge>
  : <Badge variant="destructive">Failed</Badge>;

export default function Backups() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [restoreFor, setRestoreFor] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [restoreResult, setRestoreResult] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get('/backups');
      setData(data);
      return data;
    } catch (e) { if (e.response?.status === 403) setForbidden(true); return null; }
  };
  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, []);

  const pollWhileRunning = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const d = await load();
      if (!d || !d.backups.some((b) => b.status === 'running')) clearInterval(pollRef.current);
    }, 2000);
  };

  const runNow = async () => {
    setBusy('run');
    try {
      const { data: rec } = await api.post('/backups/run');
      if (rec.status === 'completed') toast.success(`Backup completed — ${fmtBytes(rec.size_bytes)}, ${rec.total_docs} documents`);
      else if (rec.status === 'failed') toast.error(`Backup failed: ${rec.error}`);
      else { toast.info('Backup started'); pollWhileRunning(); }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Backup failed'); }
    finally { setBusy(''); }
  };

  const downloadBlob = async (b) => {
    try {
      const r = await api.get(`/backups/${b.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `robochef-backup-${b.id.slice(0, 8)}.json.gz`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy('upload');
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post('/backups/upload', fd);
      toast.success('Backup uploaded and validated');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(''); }
  };

  const doRestore = async () => {
    if (confirmText !== restoreFor.label) { toast.error('Type the exact backup label to confirm'); return; }
    setBusy('restore');
    setRestoreResult(null);
    try {
      const { data } = await api.post('/backups/restore', { backup_id: restoreFor.id, confirm_label: confirmText });
      setRestoreResult(data);
      toast.success(`Restore complete — ${data.total_docs} documents. Please log in again.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Restore failed'); }
    finally { setBusy(''); }
  };

  const backups = data?.backups || [];
  const latestOk = backups.find((b) => b.status === 'completed' && b.kind !== 'safety');
  const running = backups.some((b) => b.status === 'running');

  if (forbidden) return <div className="p-6 text-muted-foreground" data-testid="backups-forbidden">Owner access only — database backups are restricted to the owner account.</div>;

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div><h1 className="font-display text-2xl font-bold">Database Backups</h1><p className="text-sm text-muted-foreground">Weekly automatic + manual backups, with verified restore</p></div>
        <Button data-testid="backup-run-now" disabled={busy === 'run' || running} onClick={runNow}>
          {busy === 'run' || running ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <DatabaseBackup className="h-4 w-4 mr-1" />} Back up now
        </Button>
      </div>
      <PageHelp id="backups">A <b>full backup</b> of every collection runs automatically every week and is stored in private cloud storage. Restoring <b>replaces all current data</b> with the backup — a safety backup of the current state is always taken first.</PageHelp>

      <div className="grid sm:grid-cols-3 gap-3">
        <Card className="p-4 space-y-1" data-testid="latest-backup-card">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground"><ShieldCheck className="h-4 w-4" />Latest successful backup</div>
          {latestOk ? (
            <>
              <div className="font-display font-bold">{fmtDate(latestOk.created_at)}</div>
              <div className="text-sm text-muted-foreground">{fmtBytes(latestOk.size_bytes)} · {latestOk.total_docs} documents · <span className="capitalize">{latestOk.kind}</span></div>
              <Button size="sm" variant="outline" className="mt-1" data-testid="latest-backup-download" onClick={() => downloadBlob(latestOk)}><Download className="h-3.5 w-3.5 mr-1" />Download</Button>
            </>
          ) : <div className="text-sm text-muted-foreground">No backup yet — run one now.</div>}
        </Card>
        <Card className="p-4 space-y-1">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground"><CalendarClock className="h-4 w-4" />Automatic schedule</div>
          <div className="font-display font-bold" data-testid="backup-next-run">{data ? fmtDate(data.schedule.next_run) : '—'}</div>
          <div className="text-sm text-muted-foreground">Weekly · Sundays 03:00 UTC (7am Dubai)</div>
        </Card>
        <Card className="p-4 space-y-1">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground"><RefreshCw className="h-4 w-4" />Retention</div>
          <div className="font-display font-bold">{data?.schedule.retention || 12} restore points</div>
          <div className="text-sm text-muted-foreground">Newest {data?.schedule.retention || 12} completed backups are kept; older records are pruned.</div>
        </Card>
      </div>

      <Card className="p-4 space-y-2">
        <div className="font-display font-semibold">Restore from a file</div>
        <p className="text-xs text-muted-foreground">Upload a previously downloaded <code>.json.gz</code> backup. It is validated before it appears in the history below.</p>
        <Input type="file" accept=".gz" data-testid="backup-upload" disabled={busy === 'upload'} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} className="h-9 text-xs max-w-sm" />
      </Card>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Backup</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead>
            <TableHead className="text-right">Size</TableHead><TableHead className="text-right">Docs</TableHead>
            <TableHead>By</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {backups.map((b) => (
              <TableRow key={b.id} data-testid={`backup-row-${b.id}`}>
                <TableCell>
                  <div className="text-sm font-medium">{b.label}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(b.created_at)}{b.error ? ` · ${b.error}` : ''}</div>
                </TableCell>
                <TableCell><Badge variant={kindBadge(b.kind)} className="capitalize">{b.kind}</Badge></TableCell>
                <TableCell>{statusBadge(b.status)}</TableCell>
                <TableCell className="text-right font-mono text-sm">{fmtBytes(b.size_bytes)}</TableCell>
                <TableCell className="text-right font-mono text-sm">{b.total_docs ?? '—'}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{b.created_by}</TableCell>
                <TableCell>
                  <div className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Download" disabled={b.status !== 'completed'} data-testid={`backup-download-${b.id}`} onClick={() => downloadBlob(b)}><Download className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" title="Restore" disabled={b.status !== 'completed'} data-testid={`backup-restore-${b.id}`} onClick={() => { setRestoreFor(b); setConfirmText(''); setRestoreResult(null); }}><RotateCcw className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {backups.length === 0 && <div className="p-8 text-center text-muted-foreground" data-testid="backup-empty"><DatabaseBackup className="h-8 w-8 mx-auto mb-2 opacity-40" />No backups yet</div>}
      </Card>

      <Dialog open={!!restoreFor} onOpenChange={(o) => !o && setRestoreFor(null)}>
        <DialogContent className="max-w-md" data-testid="restore-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-destructive" /> Restore database</DialogTitle></DialogHeader>
          {restoreFor && (
            <div className="space-y-3">
              <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm space-y-1">
                <p>This will <b>replace ALL current data</b> (menu, orders, tables, inventory, expenses, settings…) with the contents of:</p>
                <p className="font-mono font-semibold" data-testid="restore-label">{restoreFor.label}</p>
                <p className="text-xs text-muted-foreground">{fmtDate(restoreFor.created_at)} · {fmtBytes(restoreFor.size_bytes)} · {restoreFor.total_docs} documents</p>
                <p className="text-xs">A <b>safety backup</b> of the current database is taken automatically first; if it fails, the restore aborts. Writes are blocked while the restore runs. Counts are verified after the swap.</p>
              </div>
              {!restoreResult ? (
                <>
                  <div>
                    <Label>Type the backup label exactly to confirm</Label>
                    <Input data-testid="restore-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={restoreFor.label} />
                  </div>
                  <Button variant="destructive" className="w-full" data-testid="restore-confirm-button" disabled={busy === 'restore' || confirmText !== restoreFor.label} onClick={doRestore}>
                    {busy === 'restore' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />} Restore now — replace current data
                  </Button>
                </>
              ) : (
                <div className="rounded-lg border border-success/40 bg-success/5 p-3 text-sm" data-testid="restore-result">
                  <div className="font-semibold text-success mb-1">Restore verified — {restoreResult.total_docs} documents</div>
                  <div className="text-xs text-muted-foreground max-h-36 overflow-y-auto font-mono">
                    {Object.entries(restoreResult.restored).map(([k, v]) => <div key={k}>{k}: {v}</div>)}
                  </div>
                  <div className="text-xs mt-2">Safety backup: {restoreResult.safety_backup?.slice(0, 8)}… · Reload the app to pick up restored sessions/caches.</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
